import { useState } from 'react';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InlineSpinner } from '@/components/ui/loading-spinner';
import { CalendarClock, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const BatchSelector = () => {
  const { currentBatch, batches, setCurrentBatch, loading } = useBatch();
  const [isChanging, setIsChanging] = useState(false);

  const handleBatchChange = async (batchId: string) => {
    try {
      setIsChanging(true);
      const batch = batches.find(b => b.id === batchId);
      if (batch) {
        await setCurrentBatch(batch);
      }
    } catch (err) {
      console.error('Error changing batch:', err);
    } finally {
      setIsChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-primary/10 px-4 py-2 rounded-md flex items-center">
        <InlineSpinner size="sm" className="mr-2" />
        <span className="text-sm font-medium">Loading...</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="bg-primary/10 border-0 hover:bg-primary/20">
          <CalendarClock className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium mr-1">
            Batch: <span className="font-bold">{currentBatch?.year || 'Not Set'}</span>
          </span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Select Batch</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {batches.length === 0 ? (
          <DropdownMenuItem disabled>No batches available</DropdownMenuItem>
        ) : (
          batches.map((batch) => (
            <DropdownMenuItem
              key={batch.id}
              onClick={() => handleBatchChange(batch.id)}
              disabled={isChanging || currentBatch?.id === batch.id}
              className={currentBatch?.id === batch.id ? 'bg-primary/10' : ''}
            >
              {batch.year}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/batches" className="cursor-pointer">
            Manage Batches
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BatchSelector;
